import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ApplicationProcessSection = () => {
  const steps = [
    {
      number: 1,
      title: 'Oriëntatie',
      description: 'Bepaal welke verduurzamingsmaatregel u wilt nemen',
      icon: 'Search',
      tasks: [
        'Kies uw verduurzamingsoplossing',
        'Bereken kosten en besparing',
        'Check beschikbare subsidies'
      ],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      number: 2,
      title: 'Offerte aanvragen',
      description: 'Vraag offertes aan bij erkende installateurs',
      icon: 'FileText',
      tasks: [
        'Selecteer erkende installateurs',
        'Vraag meerdere offertes aan',
        'Vergelijk prijzen en voorwaarden'
      ],
      color: 'from-green-500 to-emerald-500'
    },
    {
      number: 3,
      title: 'Subsidie aanvragen',
      description: 'Dien uw subsidieaanvraag in vóór de installatie',
      icon: 'Send',
      tasks: [
        'Verzamel benodigde documenten',
        'Dien aanvraag in via juiste kanaal',
        'Wacht op goedkeuring (2-4 weken)'
      ],
      color: 'from-purple-500 to-pink-500'
    },
    {
      number: 4,
      title: 'Installatie',
      description: 'Laat de maatregel installeren door erkende partij',
      icon: 'Wrench',
      tasks: [
        'Plan installatiedatum',
        'Zorg voor toegang tot woning',
        'Laat installatie keuren indien vereist'
      ],
      color: 'from-orange-500 to-red-500'
    },
    {
      number: 5,
      title: 'Uitbetaling aanvragen',
      description: 'Vraag uitbetaling aan met installatiebewijzen',
      icon: 'CheckCircle',
      tasks: [
        'Verzamel facturen en bewijzen',
        'Dien uitbetalingsverzoek in',
        'Ontvang subsidie (4-8 weken)'
      ],
      color: 'from-green-500 to-teal-500'
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Subsidie aanvraagproces
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Volg deze stappen voor een succesvolle subsidieaanvraag
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Connecting Line */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-200 via-purple-200 to-green-200 transform -translate-x-1/2" />

          {/* Steps */}
          <div className="space-y-12">
            {steps?.map((step, index) => (
              <motion.div
                key={step?.number}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                }`}
              >
                {/* Content */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-elevation-2 border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${step?.color} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                      <Icon name={step?.icon} size={28} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-foreground mb-2">{step?.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{step?.description}</p>
                      <ul className="space-y-2">
                        {step?.tasks?.map((task, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 + i * 0.1 }}
                            className="flex items-start space-x-2 text-sm text-muted-foreground"
                          >
                            <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                            <span>{task}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>

                {/* Step Number */}
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className={`w-16 h-16 bg-gradient-to-br ${step?.color} rounded-full flex items-center justify-center shadow-elevation-3 z-10`}
                >
                  <span className="text-2xl font-bold text-white">{step?.number}</span>
                </motion.div>

                {/* Spacer for alignment */}
                <div className="hidden lg:block flex-1" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Tips Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6"
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center">
                <Icon name="Lightbulb" size={24} className="text-green-600" />
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold text-foreground mb-3">
                Tips voor succesvolle aanvraag
              </h4>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span>Vraag subsidie aan vóór de installatie - achteraf aanvragen is vaak niet mogelijk</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span>Gebruik alleen erkende installateurs - dit is vaak een vereiste voor subsidie</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span>Bewaar alle facturen en bewijsstukken - deze heeft u nodig voor uitbetaling</span>
                </li>
                <li className="flex items-start space-x-2 text-sm text-muted-foreground">
                  <Icon name="Check" size={16} className="text-success mt-0.5" />
                  <span>Check regelmatig of er nieuwe subsidies beschikbaar zijn - regelingen wijzigen vaak</span>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ApplicationProcessSection;